import { DIRECT_LIMIT, MailChangeResult, type MailModifyOp, type MailPlanBulkOutput, type MailPlanOp } from "@kwp/contracts";
import { useCallback, useState } from "react";
import { errorMessage, useTRPCClient } from "../../lib/api";
import { usePresence } from "../../lib/use-presence";
import { useToast } from "../../ui/toast";
import { newRequestId } from "../tasks/use-task-actions";
import { describeBulkOp } from "./bulk-model";

/**
 * Bulk changes from the inbox (architecture §9): what the person selected is either explicit
 * messages or "everything matching" a query. A small organizing selection (at most 100 messages,
 * and spam or trash only within one conversation) runs at once through `mail.modify` /
 * `mail.trash`; anything bigger, and spam or trash across conversations, is a plan first
 * (`mail.planSelection` for picked messages, `mail.planBulk` for a query): the person sees the
 * plan's count and sample and applies it with `actions.applyPlan`. Destructive plans ask for the
 * passkey first; without one the runtime turns them into an approval.
 */
export type BulkTarget =
  | { kind: "selection"; messageIds: string[]; conversations: number }
  | { kind: "query"; query: string; scope: { workspaceId?: string; accountIds?: string[] } };

/** Whether this op on this selection can run directly (no plan). */
export function runsDirectly(target: BulkTarget, op: MailPlanOp): boolean {
  if (target.kind === "query") return false;
  if (target.messageIds.length > DIRECT_LIMIT) return false;
  if (op.kind === "spam" || op.kind === "trash") return target.conversations === 1;
  return true;
}

export function useBulk(onDone: () => void) {
  const client = useTRPCClient();
  const toast = useToast();
  const { ensurePresence } = usePresence();
  const [planned, setPlanned] = useState<{ out: MailPlanBulkOutput; op: MailPlanOp } | null>(null);
  const [pending, setPending] = useState(false);

  const undo = useCallback(
    async (auditId: string) => {
      try {
        const out = await client.audit.undo.mutate({ auditId });
        toast({ message: out.skipped > 0 ? `Undone, except ${out.skipped} changed since.` : "Undone." });
      } catch (e) {
        toast({ tone: "error", message: errorMessage(e) });
      }
    },
    [client, toast],
  );

  const finished = useCallback(
    (outcome: { status: "done"; result: unknown; auditId: string | null } | { status: "awaiting_approval"; approvalId: string }, verb: string) => {
      onDone();
      if (outcome.status === "awaiting_approval") {
        toast({ message: "This change needs your approval.", action: { label: "Review in Approvals", href: "/approvals" } });
        return;
      }
      const result = MailChangeResult.safeParse(outcome.result);
      const changed = result.success ? result.data.changed : null;
      const failed = result.success ? result.data.failed.length : 0;
      const message = `${verb}: ${changed === null ? "done" : `${changed} message${changed === 1 ? "" : "s"}`}${failed > 0 ? `, ${failed} refused` : ""}.`;
      const auditId = outcome.auditId;
      toast(auditId === null ? { message } : { message, action: { label: "Undo", onClick: () => void undo(auditId) } });
    },
    [onDone, toast, undo],
  );

  /** Runs the op at once, or computes the plan to show (then `apply`). */
  const run = useCallback(
    async (target: BulkTarget, op: MailPlanOp) => {
      setPending(true);
      try {
        if (target.kind === "selection" && runsDirectly(target, op)) {
          const requestId = newRequestId();
          const outcome =
            op.kind === "trash"
              ? await client.mail.trash.mutate({ requestId, input: { messageIds: target.messageIds } })
              : await client.mail.modify.mutate({ requestId, input: { messageIds: target.messageIds, op: op as MailModifyOp } });
          finished(outcome, describeBulkOp(op));
          return;
        }
        const out =
          target.kind === "query"
            ? await client.mail.planBulk.mutate({ query: target.query, ...target.scope, op })
            : await client.mail.planSelection.mutate({ messageIds: target.messageIds, op });
        setPlanned({ out, op });
      } catch (e) {
        toast({ tone: "error", message: errorMessage(e) });
      } finally {
        setPending(false);
      }
    },
    [client, finished, toast],
  );

  const apply = useCallback(async () => {
    if (planned === null || planned.out.planId === null) return;
    const planId = planned.out.planId;
    setPending(true);
    try {
      if (planned.out.capability === "destructive" && (await ensurePresence()) === "cancelled") {
        toast({ tone: "error", message: "Not applied: confirm with your passkey to apply it." });
        return;
      }
      const outcome = await client.actions.applyPlan.mutate({ planId });
      setPlanned(null);
      finished(outcome, describeBulkOp(planned.op));
    } catch (e) {
      toast({ tone: "error", message: errorMessage(e) });
    } finally {
      setPending(false);
    }
  }, [planned, client, ensurePresence, finished, toast]);

  const cancel = useCallback(() => setPlanned(null), []);
  return { run, plan: planned === null ? null : planned.out, apply, cancel, pending };
}
